import { useState, useEffect, useCallback } from 'react';
import {
  partsAPI,
  equipmentAPI,
  ordersAPI,
  customersAPI,
  dealersAPI,
  devicesAPI,
} from '../../api/api';

const apiMap = {
  part: partsAPI,
  equipment: equipmentAPI,
  order: ordersAPI,
  customer: customersAPI,
  dealer: dealersAPI,
  device: devicesAPI,
};

export const useEntityFetch = (entityType, entityId, initialData = null) => {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);

  // Always hits the server -- used for the initial load when no initialData
  // was supplied, and unconditionally for every refetch() call. refetch()
  // must never just re-apply initialData: it's called after edits/syncs
  // specifically to pick up what changed since the drawer was opened, and
  // initialData is a snapshot from whenever the caller opened the drawer.
  const fetchFromServer = useCallback(async () => {
    const api = apiMap[entityType];
    if (!api) {
      setError(`Unknown entity type: ${entityType}`);
      setLoading(false);
      return;
    }

    if (!api.getById) {
      setError(`API method getById not found for ${entityType}`);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.getById(entityId);
      setData(response.data.data);
    } catch (err) {
      console.error(`Failed to fetch ${entityType}:`, err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (!entityType || !entityId) {
      setData(null);
      setLoading(false);
      return;
    }

    if (initialData) {
      setData(initialData);
      setLoading(false);
      return;
    }

    fetchFromServer();
    // Only re-run this initial-load effect when the entity itself changes --
    // fetchFromServer is stable per entityType/entityId anyway, and
    // initialData is intentionally excluded (see refetch below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const refetch = useCallback(() => {
    if (!entityType || !entityId) return;
    fetchFromServer();
  }, [entityType, entityId, fetchFromServer]);

  return { data, loading, error, refetch };
};

export default useEntityFetch;
